import { FC, useMemo } from 'react';
import DataTable from './DataTable';
import { useTranslation } from 'react-i18next';
import { Box, Button, Tooltip, Typography, Chip } from '@mui/material';
import { AddAgreementFormData } from './dataTableV2/AddAgreementModal';
import { useEncryption } from 'contexts/EncryptionKeyContext';
import { decryptData } from 'utils/encryptionDecryptionAgreements';
import { DaysOfTheWeek } from 'components/input-fields/DaysOfWeekPicker';
import InfoIcon from '@mui/icons-material/Info';

interface DataTableProps {
  data?: any[];
  clearFilter?: string;
  onFilterChange?: (filters: any) => void;
  handleRowSelect: (rows: []) => void;
  editAgreementCallback: (id: number, data?: AddAgreementFormData | null) => void;
  encryptionKey?: string;
}

const AgreementsDataTable: FC<DataTableProps> = (props) => {
  const { t } = useTranslation();
  const { encryptionKey, setPopoverOpen } = useEncryption();
  function handleModfiyAgreement(agreementData: AddAgreementFormData, id: number) {
    props.editAgreementCallback(id, agreementData);
  };

  const getColumnShape = (data: any) => {
    // Check which optional columns should be displayed based on data
    const DEFAULT_WEEKLY_HOURS = 40;
    const hasBalance730 = data?.some((agreement: AddAgreementFormData) => {
      let balance730 = 0;
      if (agreement.balance730_encrypted && encryptionKey && encryptionKey !== '') {
        const balance = decryptData(agreement.balance730_encrypted, encryptionKey);
        if (balance !== '') {
          balance730 = parseFloat(String(balance));
          return balance730 !== 0;
        }
        return false;
      }
      if (agreement.balance730 !== null && agreement.balance730 !== undefined) {
        balance730 = parseFloat(String(agreement.balance730));
        return balance730 !== 0;
      }
      return false;
    });
    
    const hasNonDefaultWeeklyHours = data?.some((agreement: AddAgreementFormData) => {
      return agreement.weeklyWorkHours !== null && 
             agreement.weeklyWorkHours !== undefined && 
             agreement.weeklyWorkHours !== DEFAULT_WEEKLY_HOURS;
    });
    
    const hasVacation = data?.some((agreement: AddAgreementFormData) => {
      return (agreement.hoursVacation && agreement.hoursVacation > 0) ||
             (agreement.daysVacation && agreement.daysVacation > 0);
    });

    const columns: any[] = [
      {
        Header: '#',
        minWidth: 50,
        Cell: ({ row }: any) => {
          const id = Number(row.id) + 1;
          return (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.875rem'
              }}
            >
              {id}
            </Box>
          );
        }
      },

      {
        Header: t('employees.agreements.field.startDate'),
        minWidth: 110,
        Cell: ({ row }: any) => {
          const startDate = new Date(row.original.startedOn);
          return (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                {t('employees.agreements.field.startDate')}
              </Typography>
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {startDate.toLocaleDateString('it-IT')}
              </Typography>
            </Box>
          );
        }
      },

      {
        Header: t('employees.agreements.field.endDate'),
        minWidth: 110,
        Cell: ({ row }: any) => {
          if (row.original.endedOn) {
            const endDate = new Date(row.original.endedOn);
            return (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                  {t('employees.agreements.field.endDate')}
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {endDate.toLocaleDateString('it-IT')}
                </Typography>
              </Box>
            );
          }
          return (
            <Chip 
              label={t('employees.contract.types.unlimited')} 
              size="small" 
              sx={{ 
                bgcolor: 'info.main',
                color: 'white',
                fontSize: '0.7rem',
                height: '22px',
                fontWeight: 600
              }} 
            />
          );
        }
      },

      {
        Header: t('employees.agreements.field.net'),
        minWidth: 160,
        Cell: ({ row }: any) => {
          const agreement = row.original as AddAgreementFormData;
          let netDaily = 0;
          let isEncrypted = false;

          if (agreement.net_daily_encrypted) {
            const net = decryptData(agreement.net_daily_encrypted, encryptionKey);
            if (net !== '') {
              netDaily = parseFloat(String(net));
            } else {
              isEncrypted = true;
            }
          } else {
            netDaily = parseFloat(String(agreement.net_daily));
          }

          if (isEncrypted) {
            return (
              <Tooltip title={t('encryption.tooltip')} arrow>
                <Box
                  sx={{ 
                    width: 'fit-content', 
                    cursor: encryptionKey === '' ? 'pointer' : 'default',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'grey.200'
                  }}
                  onClick={() => {
                    if (encryptionKey === '') {
                      setPopoverOpen(true);
                    }
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      filter: 'blur(3px)', 
                      opacity: 0.7,
                      fontWeight: 500,
                      fontSize: '0.75rem'
                    }}
                  >
                    ・・・ €
                  </Typography>
                </Box>
              </Tooltip>
            );
          }

          const netMonthly = netDaily * 30;

          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: 'success.main',
                  color: 'white',
                  width: 'fit-content'
                }}
              >
                <Typography variant="body2" fontWeight={700} fontSize="0.875rem" color="white">
                  {netDaily.toFixed(2)} €
                </Typography>
                <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.9, color: 'white' }}>
                  /giorno
                </Typography>
              </Box>
              {encryptionKey && encryptionKey !== '' && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 'fit-content'
                  }}
                >
                  <Typography variant="body2" fontWeight={700} fontSize="0.875rem" color="white">
                    {netMonthly.toFixed(2)} €
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.9, color: 'white' }}>
                    /mese
                  </Typography>
                </Box>
              )}
            </Box>
          );
        },
      },
    ];

    // Add weekly work hours column only if at least one agreement has non-default hours
    if (hasNonDefaultWeeklyHours) {
      columns.push({
        Header: t('employees.agreements.field.weeklyWorkHours'),
        minWidth: 100,
        Cell: ({ row }: any) => {
          const agreement = row.original as AddAgreementFormData;
          const hours = agreement.weeklyWorkHours;
          // If hours are set and different from default, show them
          if (hours !== null && hours !== undefined && hours !== DEFAULT_WEEKLY_HOURS) {
            return (
              <Chip
                label={`${hours}h`}
                size="small"
                sx={{
                  bgcolor: 'warning.main',
                  color: 'white',
                  fontSize: '0.75rem',
                  height: '24px',
                  fontWeight: 600
                }}
              />
            );
          }
          // Otherwise show default (either null/undefined or equals default)
          return (
            <Typography variant="body2" color="text.secondary" fontStyle="italic" fontSize="0.75rem">
              {DEFAULT_WEEKLY_HOURS}h
            </Typography>
          );
        },
      });
    }

    // Add vacation column only if at least one agreement has vacation set
    if (hasVacation) {
      columns.push({
        Header: t('employees.agreements.vacation'),
        minWidth: 120,
        Cell: ({ row }: any) => {
          const agreement = row.original as AddAgreementFormData;
          if (agreement.hoursVacation && agreement.hoursVacation > 0) {
            return (
              <Chip
                label={`${agreement.hoursVacation}h`}
                size="small"
                sx={{
                  bgcolor: 'info.main',
                  color: 'white',
                  fontSize: '0.7rem',
                  height: '24px',
                  fontWeight: 600
                }}
              />
            );
          } else if (agreement.daysVacation && agreement.daysVacation > 0) {
            // Get the first letter of the translated "days" word for abbreviation
            const daysLabel = t('common.days');
            const dayAbbr = daysLabel.charAt(0).toLowerCase();
            return (
              <Chip
                label={`${agreement.daysVacation}${dayAbbr}`}
                size="small"
                sx={{
                  bgcolor: 'info.main',
                  color: 'white',
                  fontSize: '0.7rem',
                  height: '24px',
                  fontWeight: 600
                }}
              />
            );
          }
          return (
            <Typography variant="body2" color="text.secondary" fontStyle="italic" fontSize="0.75rem">
              -
            </Typography>
          );
        },
      });
    }

    // Add balance 730 column only if encryption key is set AND at least one agreement has balance730
    if (encryptionKey && encryptionKey !== '' && hasBalance730) {
      columns.push({
        Header: t('employees.agreements.field.balance730'),
        minWidth: 140,
        Cell: ({ row }: any) => {
          const agreement = row.original as AddAgreementFormData;
          let balance730 = 0;

          if (agreement.balance730_encrypted) {
            const balance = decryptData(agreement.balance730_encrypted, encryptionKey);
            if (balance !== '') {
              balance730 = parseFloat(String(balance));
            } else {
              return (
                <Tooltip title={t('encryption.tooltip')} arrow>
                  <Box
                    sx={{
                      width: 'fit-content',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                    onClick={() => {
                      if (encryptionKey === '') {
                        setPopoverOpen(true);
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        filter: 'blur(3px)',
                        opacity: 0.7,
                        fontWeight: 500
                      }}
                    >
                      ・・・ €
                    </Typography>
                  </Box>
                </Tooltip>
              );
            }
          } else if (agreement.balance730 !== null && agreement.balance730 !== undefined) {
            balance730 = parseFloat(String(agreement.balance730));
          } else {
            return (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                -
              </Typography>
            );
          }

          // Don't show if balance is 0
          if (balance730 === 0) {
            return (
              <Typography variant="body2" color="text.secondary" fontStyle="italic" fontSize="0.75rem">
                -
              </Typography>
            );
          }

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={`${balance730 > 0 ? '+' : ''}${balance730.toFixed(2)} €`}
                size="small"
                sx={{
                  bgcolor: balance730 > 0 ? 'success.main' : 'error.main',
                  color: 'white',
                  fontSize: '0.7rem',
                  height: '24px',
                  fontWeight: 700
                }}
              />
              {agreement.balance730_date && (
                <Tooltip
                  title={`${t('employees.agreements.field.balance730Date')}: ${new Date(agreement.balance730_date).toLocaleDateString('it-IT')}`}
                  arrow
                >
                  <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          );
        },
      });
    }

    columns.push(
      {
        Header: t('employees.agreements.field.daysOff'),
        minWidth: 100,
        Cell: ({ row }: any) => {
          const agreement = row.original as AddAgreementFormData;
          
          // To order the days
          const daysOfWeekOrder = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
          const sortedDays = (agreement.freeDays as unknown as DaysOfTheWeek[]).sort((a, b) => {
            return daysOfWeekOrder.indexOf(a) - daysOfWeekOrder.indexOf(b);
          });

          let freeDaysText = "";
          for (const day of sortedDays) {
            freeDaysText += t('employees.agreements.day.' + day);
            freeDaysText += ", ";
          }
          freeDaysText = freeDaysText.slice(0, -2);
          
          return (
            <Tooltip title={freeDaysText || '-'} arrow>
              <Chip 
                label={agreement.daysOff}
                size="small" 
                sx={{ 
                  bgcolor: 'secondary.main',
                  color: 'white',
                  fontSize: '0.7rem',
                  height: '24px',
                  fontWeight: 600
                }} 
              />
            </Tooltip>
          );
        },
      },
    );

    // Add notes column only if encryption key is set
    if (encryptionKey && encryptionKey !== '') {
      columns.push({
        Header: t('room.notes'),
        minWidth: 180,
        Cell: ({ row }: any) => {
          const agreement = row.original as AddAgreementFormData;
          const notes = agreement.notes;
          if (notes && notes.trim().length > 0) {
            const truncatedNotes = notes.length > 40 ? notes.substring(0, 40) + '...' : notes;
            return (
              <Tooltip title={notes} arrow placement="top">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'grey.100',
                    maxWidth: '180px',
                    cursor: 'help'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem',
                      color: 'text.primary'
                    }}
                  >
                    {truncatedNotes}
                  </Typography>
                </Box>
              </Tooltip>
            );
          }
          return (
            <Typography variant="body2" color="text.secondary" fontStyle="italic" fontSize="0.75rem">
              -
            </Typography>
          );
        },
      });
    }

    columns.push({
        Header: t('employees.agreements.dataTable.modify'),
        minWidth: 150,
        Cell: ({ row }: any) => {
          const agreement = row.original as AddAgreementFormData;
          let isEncrypted = false;
          if (agreement.net_daily_encrypted) {
            const net = decryptData(agreement.net_daily_encrypted, encryptionKey);
            isEncrypted = net === '';
          }
          return (
            <Button
              variant="contained"
              size="small"
              disabled={isEncrypted}
              onClick={() => {
                handleModfiyAgreement(row.original, row.original.id);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.7rem',
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                minWidth: 'auto',
                bgcolor: isEncrypted ? 'grey.300' : 'primary.main',
                '&:hover': {
                  bgcolor: isEncrypted ? 'grey.300' : 'primary.dark'
                }
              }}
            >
              {t('common.tables.button.editWithItem', {
                item: t('employees.agreements.name')
              })}
            </Button>
          );
        },
      }
    );

    return columns;
  };
  const columns: any = useMemo(() => getColumnShape(props.data), [t, encryptionKey, setPopoverOpen, props.data]);

  return DataTable({
    ...props,
    columns,
  });
};

export default AgreementsDataTable;
